import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import NavBar from "./NavBar";
import img1 from "./images/1.png";
import img2 from "./images/2.png";
import img3 from "./images/3.png";
import img4 from "./images/4.png";
import img5 from "./images/5.png";
import img6 from "./images/6.png";
import img7 from "./images/7.png";
import img8 from "./images/8.png";
import heroImage from "./images/index.jpeg";

export default function LashStudio() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();

  const handleNavClick = (sectionId) => {
    if (location.pathname !== "/") {
      navigate(`/#${sectionId}`);
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const images = [img1, img2, img3, img4, img5, img6, img7, img8];

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  const openModal = (index) => {
    setCurrentIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleModalClick = (e) => {
    if (e.target.id === "modal-background") {
      closeModal();
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F5EFE6]">
      <NavBar onNavClick={handleNavClick} />
      <main>
        {/* Hero Section */}
        <section id="top" className="relative h-screen bg-cover bg-center flex items-center justify-center" style={{ backgroundImage: `url(${heroImage})` }}>
          <div className="bg-[#F5EFE6] bg-opacity-50 w-full h-full absolute inset-0"></div>
          <div className="relative z-10 text-center text-[#4A4A4A] p-6">
            <h2 className="text-4xl sm:text-6xl font-bold mb-4 tracking-wide">{t('heroTitle')}</h2>
            <p className="text-lg sm:text-2xl mb-6 font-light">{t('heroSubtitle')}</p>
            <a href="sms:+16468203551" className="bg-gradient-to-r from-[#D8B4A0] to-[#B19787] text-white px-10 py-4 rounded-full shadow-lg hover:from-[#CFA694] hover:to-[#A88D7F] transition-transform transform hover:scale-105 text-lg">
              {t('bookSession')}
            </a>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-20 bg-[#F5EFE6] text-center">
          <div className="max-w-4xl mx-auto px-6">
            <h3 className="text-4xl font-semibold text-[#4A4A4A] mb-6 uppercase tracking-widest">{t('aboutUs')}</h3>
            <p className="text-lg text-[#6E6E6E] leading-relaxed">{t('aboutUsDescription')}</p>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h3 className="text-4xl font-semibold text-[#4A4A4A] text-center mb-16 uppercase tracking-widest">{t('ourServices')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              <ServiceCard title={t('classicLash')} description={t('classicLashDesc')} />
              <ServiceCard title={t('volumeLash')} description={t('volumeLashDesc')} />
              <ServiceCard title={t('lashLift')} description={t('lashLiftDesc')} />
              <ServiceCard title={t('underLash')} description={t('underLashDesc')} />
              <ServiceCard title={t('hybridLash')} description={t('hybridLashDesc')} />
            </div>
            <div className="text-center mt-10">
              <button onClick={() => navigate('/menu')} className="bg-gradient-to-r from-[#D8B4A0] to-[#B19787] text-white px-8 py-3 rounded-full shadow-lg hover:from-[#CFA694] hover:to-[#A88D7F] transition-transform transform hover:scale-105 text-lg">
                {t('viewMenu')}
              </button>
            </div>
          </div>
        </section>

        {/* Gallery Section */}
        <section id="gallery" className="py-20 bg-[#F5EFE6] text-center">
          <div className="max-w-7xl mx-auto px-6">
            <h3 className="text-4xl font-semibold text-[#4A4A4A] mb-10 uppercase tracking-widest">{t('gallery')}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {images.map((image, index) => (
                <img key={index} src={image} alt={`Gallery ${index + 1}`} className="w-full h-48 object-cover rounded-xl cursor-pointer shadow-md hover:scale-105 transition-transform" onClick={() => openModal(index)} />
              ))} 
            </div>

            {isModalOpen && (
              <div id="modal-background" onClick={handleModalClick} className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
                <button onClick={closeModal} className="absolute top-4 right-4 text-white text-3xl">&times;</button>
                <button onClick={prevSlide} className="absolute left-4 text-white text-4xl">❮</button>
                <img src={images[currentIndex]} alt={`Gallery ${currentIndex + 1}`} className="w-11/12 sm:w-3/4 md:w-2/4 lg:w-1/3 max-h-[80vh] object-contain rounded-xl shadow-lg" />
                <button onClick={nextSlide} className="absolute right-4 text-white text-4xl">❯</button>
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

function ServiceCard({ title, description }) {
  return (
    <div className="p-8 rounded-xl shadow-lg border border-gray-200 text-center bg-[#FDF8F3] hover:shadow-xl transition-shadow">
      <h4 className="text-2xl font-semibold text-[#4A4A4A] mb-4">{title}</h4>
      <p className="text-[#6E6E6E] text-base leading-relaxed">{description}</p>
    </div>
  );
}
